import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { ChangeEvent, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useCampaignFormFieldFiltersQuery,
  useCampaignFormFieldLabelsQuery,
  useCampaignFormFieldsQuery,
  useDownloadFilteredResponses,
  useExportResponseAttachments,
  useGetFilteredResponses,
} from "../../../App/Queries/CampaignQueries";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useDebouncedEffect } from "../../../App/State/Hooks/useDebouncedEffect";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { IGetFilteredResponsesQuery } from "../../../App/Typings/Campaigns";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import {
  getSessionStorage,
  setSessionStorage,
  STORAGE_VERSION_DATE,
} from "../../../App/Utilities/browserStorage";

dayjs.extend(customParseFormat);

export type CandidatesCapturedState = ReturnType<typeof useCandidatesCapturedState>;
type CandidatesCapturedFilterState = Pick<IGetFilteredResponsesQuery, "paginationRequest" | "rules">;

const defaultRules: Rule[] = [
  {
    filter: {
      command: "",
      field: "",
      type: "",
      value: "",
    },
    id: "initial-rule",
    rules: [],
  },
];

const defaultFilterState: CandidatesCapturedFilterState = {
  paginationRequest: {
    page: 0,
    pageSize: 10,
    search: "",
    sortBy: "dateAdded",
    sortDirection: "desc",
  },
  rules: defaultRules,
};

function getNotificationDateRule(searchParams: URLSearchParams): Rule | null {
  const startDate = searchParams.get("startdate");
  const endDate = searchParams.get("enddate");

  if (!startDate || !endDate) {
    return null;
  }

  const start = dayjs(startDate, "YYYY-MM-DD", true);
  const end = dayjs(endDate, "YYYY-MM-DD", true);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  return {
    id: "notification-date-filter",
    filter: {
      type: "datetime",
      field: "campaignResponses.CandidateResponse.dateAdded",
      command: "between",
      value: {
        type: "datetime",
        value: {
          item1: new Date(`${startDate}T00:00:00.000Z`).toISOString(),
          item2: new Date(`${endDate}T23:59:59.999Z`).toISOString(),
        },
      },
    },
    rules: [],
  };
}

export default function useCandidatesCapturedState(campaignId = "", formId = "", queryEnabled = true) {
  const [searchParams] = useSearchParams();
  const { paginationRequest, rules: storedRules } =
    getSessionStorage<CandidatesCapturedFilterState>(`form-${campaignId}-${STORAGE_VERSION_DATE}`) ??
    defaultFilterState;
  const notificationDateRule = useMemo(() => getNotificationDateRule(searchParams), [searchParams]);

  const [search, setSearch] = useState<string>(paginationRequest.search ?? "");
  const [page, setPage] = useState(paginationRequest.page);
  const [pageSize, setPageSize] = useState(paginationRequest.pageSize);
  const [order, setOrder] = useState<"asc" | "desc">(
    (paginationRequest.sortDirection as "asc" | "desc") ?? "desc",
  );
  const [orderBy, setOrderBy] = useState<string | undefined>(paginationRequest.sortBy ?? "dateAdded");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [rules, setRules] = useState<Rule[]>(notificationDateRule ? [notificationDateRule] : storedRules);

  const { data: formFields, isLoading: isFormFieldsLoading } = useCampaignFormFieldFiltersQuery(
    campaignId,
    formId,
    "CandidateResponse",
    queryEnabled,
  );
  const { data: labelHistory, isLoading: isLabelHistoryLoading } = useCampaignFormFieldLabelsQuery(
    campaignId,
    formId,
    queryEnabled,
  );
  const { data: formFieldsQuery = [] } = useCampaignFormFieldsQuery(campaignId, formId, queryEnabled);
  const responseRequest = useMemo(
    () =>
      ({
        campaignId,
        formId,
        paginationRequest: {
          page: page + 1,
          pageSize,
          search,
          sortBy: orderBy,
          sortDirection: order,
        },
        rules,
        formFields: formFieldsQuery
          ? formFieldsQuery.reduce((acc, field) => ({ ...acc, [field.id]: field }), {})
          : null,
      }) as IGetFilteredResponsesQuery,
    [campaignId, formId, page, pageSize, rules, orderBy, order, search, formFieldsQuery],
  );
  const { data, isLoading } = useGetFilteredResponses(responseRequest, queryEnabled);
  const { mutateAsync: downloadResponses, isLoading: downloadProcessing } = useDownloadFilteredResponses();
  const { mutateAsync: exportAttachments, isLoading: exportProcessing } = useExportResponseAttachments();
  const { displayError, displaySuccess } = useNotification();

  const getDataType = (key: string) => {
    if (key.includes("date") || key.includes("time")) return DataType.Date;
    if (key === "interestpicker" || key === "areaofinterest") return DataType.Object;

    return DataType.String;
  };

  const getSortDirection = (key: string) => {
    if (key === orderBy) {
      const sortDirection = order === "asc" ? SortDirection.Ascend : SortDirection.Descend;
      return sortDirection;
    }
    return undefined;
  };

  const columnNames = useMemo(() => {
    const columnMap = new Map<string, Column>();
    const usedTitles = new Set<string>();

    // Process current form fields to establish base columns
    for (const fieldQuery of formFieldsQuery) {
      if (!columnMap.has(fieldQuery.id)) {
        columnMap.set(fieldQuery.id, {
          title: fieldQuery.label,
          key: fieldQuery.id,
          dataType: getDataType(fieldQuery.id.toLowerCase()),
          sortDirection: getSortDirection(fieldQuery.id),
        });
        usedTitles.add(fieldQuery.label);
      }
    }

    // Process labelHistory to add additional columns from older versions
    if (labelHistory && !isLabelHistoryLoading) {
      for (const [key, labelSet] of Object.entries(labelHistory)) {
        const labelsArray = Array.from(labelSet).reverse();

        if (labelsArray.length === 1) {
          // Single label - only add if the title hasn't been used yet
          const label = labelsArray[0];
          if (!usedTitles.has(label)) {
            columnMap.set(key, {
              title: label,
              key,
              dataType: getDataType(key.toLowerCase()),
              sortDirection: getSortDirection(key),
            });
            usedTitles.add(label);
          }
        } else {
          //  Multiple labels, add each unique label that hasn't been used
          let suffixCounter = 0;
          for (let i = 0; i < labelsArray.length; i++) {
            const label = labelsArray[i];
            if (!usedTitles.has(label)) {
              let columnKey;
              if (suffixCounter === 0 && !columnMap.has(key)) {
                columnKey = key;
              } else {
                columnKey = `${key}_${suffixCounter + 1}`;
              }

              columnMap.set(columnKey, {
                title: label,
                key: columnKey,
                dataType: getDataType(key.toLowerCase()), // Use original key for data type
                sortDirection: getSortDirection(columnKey),
              });
              usedTitles.add(label);
              suffixCounter++;
            }
          }
        }
      }
    }

    return Array.from(columnMap.values());
  }, [labelHistory, isLabelHistoryLoading, formFieldsQuery]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSetRules = (newRules: Rule[]) => {
    setRules(newRules);
    setPage(0);
  };

  const handleSetSearch = (newSearch: string) => {
    if (search !== newSearch) {
      setSearch(newSearch);
      setPage(0);
    }
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRemoveRule = (id: string) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  };

  const handleResetRules = () => {
    setRules(defaultRules);
  };

  const handleDownloadResponses = useDebouncedHandler(async () => {
    if (downloadProcessing) {
      return;
    }
    await downloadResponses(responseRequest);
  }, 200);

  const handleDownloadResponseAttachments = useDebouncedHandler(async () => {
    if (exportProcessing) {
      return;
    }
    await exportAttachments(responseRequest, {
      onSuccess: (successResponse) => {
        if (successResponse.success) {
          displaySuccess("Exporting attachments, you will receive an email shortly.");
        } else {
          displayError(successResponse.message ?? "Error exporting attachments, please try again.");
        }
      },
      onError: () => {
        displayError("Error exporting attachments, please contact support.");
      },
    });
  }, 200);

  useDebouncedEffect(
    () => {
      setSessionStorage<CandidatesCapturedFilterState>(`form-${campaignId}-${STORAGE_VERSION_DATE}`, {
        paginationRequest: { page, pageSize, search, sortBy: orderBy, sortDirection: order },
        // Exclude the notification-seeded date rule - it's ephemeral (derived from URL params)
        // and shouldn't outlive the session it was seeded in.
        rules: rules.filter((rule) => rule.id !== "notification-date-filter"),
      });
    },
    [page, pageSize, search, orderBy, order, JSON.stringify(rules)],
    100,
  );

  return {
    getters: {
      isFilterModalOpen,
      campaignResponses: data,
      campaignResponsesLoading: isLoading,
      columnNames,
      formFields,
      isFormFieldsLoading,
      order,
      orderBy,
      page,
      pageSize,
      rules,
      downloadProcessing,
      search,
    },
    handlers: {
      handleChangePage,
      handleChangeRowsPerPage,
      handleOpenFilterModal,
      handleCloseFilterModal,
      handleRequestSort,
      handleResetRules,
      handleRemoveRule,
      handleSetRules,
      handleSetSearch,
      handleDownloadResponses,
      handleDownloadResponseAttachments,
    },
  } as const;
}
